import * as PIXI from 'pixi.js';
import { StoryManager, InputManager } from '@/systems';

interface DayBlock {
  label: string;
  startProgress: number;
  endProgress: number;
}

const DAY_BLOCKS: DayBlock[] = [
  { label: 'Hari 1', startProgress: 0.0, endProgress: 0.3125 },
  { label: 'Hari 2', startProgress: 0.375, endProgress: 0.5625 },
  { label: 'Hari 3', startProgress: 0.625, endProgress: 1.0 },
];

export class DayDisplay {
  private container: PIXI.Container;
  private background: PIXI.Sprite;
  private fallbackBackground: PIXI.Graphics;
  private descriptionText: PIXI.Text;
  private objectiveTitle: PIXI.Text;
  private storyManager: StoryManager;

  // Progress bar elements
  private progressContainer: PIXI.Container;
  private progressTitle: PIXI.Text;
  private progressBarBorder: PIXI.Graphics;
  private progressBarBackground: PIXI.Graphics;
  private progressBarFill: PIXI.Container;
  private progressFillGraphics: PIXI.Graphics;
  private progressNoiseSprite: PIXI.Sprite | null = null;
  private blockDividers: PIXI.Graphics;
  private blockLabels: PIXI.Text[] = [];

  // Separator line between progress and objective
  private separatorRect: PIXI.Graphics;


  // Layout constants
  private readonly PANEL_WIDTH = 550;
  private readonly BAR_X = 20;
  private readonly BAR_WIDTH = 510;
  private readonly BAR_HEIGHT = 36;
  private readonly BAR_Y = 60;
  private readonly BAR_RADIUS = 8;
  private readonly BAR_PADDING = 3;
  private readonly FILL_INSET = 0;

  constructor() {
    this.storyManager = StoryManager.getInstance();
    this.container = new PIXI.Container();

    this.fallbackBackground = new PIXI.Graphics();
    this.fallbackBackground.beginFill(0x000000, 0.8);
    this.fallbackBackground.drawRoundedRect(0, 0, this.PANEL_WIDTH, 250, 10);
    this.fallbackBackground.endFill();

    this.background = new PIXI.Sprite(PIXI.Texture.EMPTY);
    this.background.width = 600;
    this.background.height = 250;

    // Progress Title
    this.progressTitle = new PIXI.Text('Progress', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 28,
      fill: 0xffffff,
      fontWeight: 'bold',
      padding: 4,
    });
    this.progressTitle.resolution = window.devicePixelRatio || 2;
    this.progressTitle.x = 20;
    this.progressTitle.y = 12;

    // Progress Bar
    this.progressContainer = new PIXI.Container();
    this.progressContainer.x = 0;
    this.progressContainer.y = 0;

    // Bar background
    this.progressBarBackground = new PIXI.Graphics();
    this.progressBarBackground.beginFill(0x3B2816, 1);
    this.progressBarBackground.drawRoundedRect(
      this.BAR_X + this.BAR_PADDING, this.BAR_Y + this.BAR_PADDING,
      this.BAR_WIDTH - this.BAR_PADDING * 2, this.BAR_HEIGHT - this.BAR_PADDING * 2,
      this.BAR_RADIUS - this.BAR_PADDING
    );
    this.progressBarBackground.endFill();

    // Bar fill container
    this.progressBarFill = new PIXI.Container();
    this.progressFillGraphics = new PIXI.Graphics();

    // Block dividers
    this.blockDividers = new PIXI.Graphics();

    // Bar border
    this.progressBarBorder = new PIXI.Graphics();
    this.progressBarBorder.beginFill(0xD4AC0D, 1);
    this.progressBarBorder.drawRoundedRect(this.BAR_X, this.BAR_Y, this.BAR_WIDTH, this.BAR_HEIGHT, this.BAR_RADIUS);
    this.progressBarBorder.endFill();

    this.separatorRect = new PIXI.Graphics();

    // Block labels
    this.createBlockLabels();

    this.progressContainer.addChild(this.progressBarBorder);
    this.progressContainer.addChild(this.progressBarBackground);
    this.progressContainer.addChild(this.progressBarFill);
    this.progressContainer.addChild(this.blockDividers);
    this.progressContainer.addChild(this.separatorRect);
    for (const label of this.blockLabels) {
      this.progressContainer.addChild(label);
    }

    this.drawBlockDividers();

    // Objective
    this.objectiveTitle = new PIXI.Text('Objective', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 26,
      fill: 0xffffff,
      fontWeight: 'bold',
      padding: 4,
    });
    this.objectiveTitle.resolution = window.devicePixelRatio || 2;
    this.objectiveTitle.x = 20;
    this.objectiveTitle.y = 110;

    // Objective
    this.descriptionText = new PIXI.Text('', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 22,
      fill: 0xcccccc,
      wordWrap: true,
      wordWrapWidth: this.PANEL_WIDTH - 60,
      align: 'left',
      padding: 4,
    });
    this.descriptionText.resolution = window.devicePixelRatio || 2;
    this.descriptionText.anchor.set(0, 0);
    this.descriptionText.x = 20;
    this.descriptionText.y = 145;

    this.container.addChild(this.fallbackBackground);
    this.container.addChild(this.background);
    this.container.addChild(this.progressTitle);
    this.container.addChild(this.progressContainer);
    this.container.addChild(this.objectiveTitle);
    this.container.addChild(this.descriptionText);

    this.container.visible = true;

    this.container.x = 0;
    this.container.y = 0;

    this.storyManager.addDayEventListener('dayChanged', () => {
      this.updateDisplay();
    });

    this.initialization();
  }

  private async initialization() {
    await this.initializeBackgroundTexture();
    await this.createNoiseTexture();
    this.drawSeparatorLine();
    await this.updateDisplay();
  }

  private async createNoiseTexture(): Promise<void> {
  try {
    const img = new Image();
    img.src = '/assets/ui/Bar.png';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
    });

    const x = this.BAR_X + this.BAR_PADDING;
    const y = this.BAR_Y + this.BAR_PADDING;
    const w = this.BAR_WIDTH - this.BAR_PADDING * 2;
    const h = this.BAR_HEIGHT - this.BAR_PADDING * 2;
    const r = this.BAR_RADIUS;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;

    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(w - r, 0);
    ctx.arcTo(w, 0, w, r, r);
    ctx.lineTo(w, h - r);
    ctx.arcTo(w, h, w - r, h, r);
    ctx.lineTo(r, h);
    ctx.arcTo(0, h, 0, h - r, r);
    ctx.lineTo(0, r);
    ctx.arcTo(0, 0, r, 0, r);
    ctx.closePath();
    ctx.clip();
    const zoomX = 1.05;
    const zoomY = 1.2
    const drawW = w * zoomX;
    const drawH = h * zoomY;
    const drawX = -(drawW - w) / 2;
    const drawY = -(drawH - h) / 2;

    ctx.drawImage(img, drawX, drawY, drawW, drawH);

    const texture = PIXI.Texture.from(canvas);
    this.progressNoiseSprite = new PIXI.Sprite(texture);
    this.progressNoiseSprite.x = x;
    this.progressNoiseSprite.y = y;
    this.progressNoiseSprite.width = w;
    this.progressNoiseSprite.height = h;

    this.progressBarFill.addChild(this.progressNoiseSprite);
    this.progressBarFill.addChild(this.progressFillGraphics);
  } catch (e) {
    console.error('Gagal load Bar.png:', e);
  }
}

  private createBlockLabels(): void {
    const blockCount = DAY_BLOCKS.length;
    const innerWidth = this.BAR_WIDTH - this.FILL_INSET * 2;
    const blockWidth = innerWidth / blockCount;
    const innerX = this.BAR_X + this.FILL_INSET;

    for (let i = 0; i < blockCount; i++) {
      const label = new PIXI.Text(DAY_BLOCKS[i].label, {
        fontFamily: 'Rosarivo, serif',
        fontSize: 18,
        fill: 0xffffff,
        fontWeight: 'bold',
        padding: 2,
      });
      label.resolution = window.devicePixelRatio || 2;
      label.anchor.set(0.5, 0.5);
      label.x = innerX + blockWidth * i + blockWidth / 2;
      label.y = this.BAR_Y + this.BAR_HEIGHT / 2;
      this.blockLabels.push(label);
    }
  }

  private drawBlockDividers(): void {
    this.blockDividers.clear();

    const blockCount = DAY_BLOCKS.length;
    const innerWidth = this.BAR_WIDTH - this.FILL_INSET * 2;
    const blockWidth = innerWidth / blockCount;
    const innerX = this.BAR_X + this.FILL_INSET;
    const sepY = this.BAR_Y + this.BAR_PADDING;
    const sepHeight = this.BAR_HEIGHT - this.BAR_PADDING * 2;

    for (let i = 1; i < blockCount; i++) {
      const x = innerX + blockWidth * i;

      this.blockDividers.beginFill(0x3B2816, 1);
      this.blockDividers.drawRect(x - 2, sepY, 5, sepHeight);
      this.blockDividers.endFill();
    }
  }

  private async initializeBackgroundTexture(): Promise<void> {
    this.updateBackgroundSize(200);
    this.background.visible = true;
    this.fallbackBackground.visible = false;
  }

  private updateBackgroundSize(height: number): void {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      const gradient = ctx.createLinearGradient(0, 0, 600, 0);
      gradient.addColorStop(0, '#493E2A');
      gradient.addColorStop(0.5, '#493E2A');
      gradient.addColorStop(1, 'rgba(73, 62, 42, 0)');

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 600, height);

      const texture = PIXI.Texture.from(canvas);
      this.background.texture = texture;
      this.background.width = 600;
      this.background.height = height;
    }
  }

  /**
   * Maps the overall progress (0.0-1.0) to a visual fill width across the 3 blocks.
   * The fill spans proportionally within each block based on the day's progress range.
   */
  private getVisualFillWidth(progress: number): number {
    const blockCount = DAY_BLOCKS.length;
    const innerWidth = this.BAR_WIDTH - this.FILL_INSET * 2;
    const blockWidth = innerWidth / blockCount;

    for (let i = 0; i < blockCount; i++) {
      const block = DAY_BLOCKS[i];
      if (progress <= block.endProgress) {
        const blockRange = block.endProgress - block.startProgress;
        const progressInBlock = blockRange > 0
          ? Math.max(0, (progress - block.startProgress) / blockRange)
          : 0;

        return blockWidth * i + blockWidth * progressInBlock;
      }
    }

    return innerWidth;
  }

  private updateProgressBar(progress: number): void {
  this.progressFillGraphics.clear();

  const fillWidth = this.getVisualFillWidth(progress);
  const x = this.BAR_X + this.BAR_PADDING;
  const y = this.BAR_Y + this.BAR_PADDING;
  const w = this.BAR_WIDTH - this.BAR_PADDING * 2;
  const h = this.BAR_HEIGHT - this.BAR_PADDING * 2;
  const r = this.BAR_RADIUS;

  if (this.progressNoiseSprite) {
    this.progressNoiseSprite.x = x;
    this.progressNoiseSprite.y = y;
    this.progressNoiseSprite.width = w;
    this.progressNoiseSprite.height = h;
  }

  const coverWidth = w - fillWidth;
  if (coverWidth > 0) {
    this.progressFillGraphics.beginFill(0x3B2816, 1);
    this.progressFillGraphics.drawRoundedRect(x + fillWidth, y, coverWidth, h, r);
    this.progressFillGraphics.endFill();

    this.progressFillGraphics.beginFill(0x3B2816, 1);
    this.progressFillGraphics.drawRect(x + fillWidth, y, r, h);
    this.progressFillGraphics.endFill();
  }

  this.updateLabelColors(fillWidth);
}

  private updateLabelColors(fillWidth: number): void {
    const innerX = this.BAR_X + this.FILL_INSET;

    for (let i = 0; i < this.blockLabels.length; i++) {
      const label = this.blockLabels[i];
      const labelCenterRelative = label.x - innerX;

      if (fillWidth >= labelCenterRelative && fillWidth > 0) {
        label.style.fill = 0x3B2816;
      } else {
        label.style.fill = 0xffffff;
      }
    }
  }

  private updateDisplay(): void {
    const currentDay = this.storyManager.getCurrentDay();
    const dayInfo = this.storyManager.getDayInfo(currentDay);
    const progress = this.storyManager.getCurrentProgress();

    this.updateProgressBar(progress);

    if (dayInfo) {
      this.descriptionText.text = dayInfo.description || '';
    } else {
      this.descriptionText.text = '';
    }

    const padding = 20;
    const contentBottom = this.descriptionText.y + this.descriptionText.height + padding;
    this.updateBackgroundSize(contentBottom);
  }

  public updatePosition(screenWidth: number, _screenHeight: number): void {
    const referenceWidth = 1280;
    const isMobile = screenWidth < 1024;
    const isTablet = InputManager.getInstance().isTablet();
    const minScale = isTablet ? 0.35 : (isMobile ? 0.25 : 0.4);
    const maxScale = isTablet ? 0.55 : (isMobile ? 0.42 : 0.7);

    let scale = screenWidth / referenceWidth;
    scale = Math.max(minScale, Math.min(maxScale, scale));

    this.container.scale.set(scale);

    // Align to Left
    this.container.x = 0;
    this.container.y = 30;
  }

  private drawSeparatorLine(): void {
    const separatorY = this.BAR_Y + this.BAR_HEIGHT + 24;

    this.separatorRect.clear();
    this.separatorRect.beginFill(0xC9A84C, 0.6);
    this.separatorRect.drawRect(20, separatorY, this.PANEL_WIDTH - 40, 1.5);
    this.separatorRect.endFill();

    this.objectiveTitle.y = separatorY + 24;
    this.descriptionText.y = separatorY + 60;
  }

  public getContainer(): PIXI.Container {
    return this.container;
  }

  public show(): void {
    this.container.visible = true;
  }

  public hide(): void {
    this.container.visible = false;
  }

  public setVisible(visible: boolean): void {
    this.container.visible = visible;
  }

  public destroy(): void {
    this.container.destroy();
  }
}
